// In SectionGridFeaturePlaces.tsx

import { FC, ReactNode } from "react";
import { StayDataType } from "data/types";
import StayCard from "components/StayCard/StayCard";
import { useSelector } from "react-redux";
import { RootState } from "redux/store";
import SectionGridFeaturePlacesSkeleton from "shared/Skelton/SectionGirdSkelton";
import { Star } from "lucide-react";

export interface SectionGridFeaturePlacesProps {
  stayListings?: any;
  gridClass?: string;
  heading?: ReactNode;
  subHeading?: ReactNode;
}

const SectionGridFeaturePlaces: FC<SectionGridFeaturePlacesProps> = ({
  stayListings,
  gridClass = "",
  heading = "Featured Properties",
  subHeading = "Handpicked stays curated by our team for exceptional experiences",
}) => {
  // ✅ Only get loading state from Redux (don't dispatch here)
  const { loading } = useSelector((state: RootState) => state.property);

  // ✅ Use the prop passed from PageHome
  const properties = stayListings || [];

  console.log("🎯 SectionGridFeaturePlaces rendering:", {
    stayListings,
    properties,
    count: properties?.length,
    loading
  });

  // Loading state
  if (loading) {
    return <SectionGridFeaturePlacesSkeleton />;
  }

  // Empty state - Don't render section
  if (!properties || properties.length === 0) {
    console.log("ℹ️ No featured properties to display");
    return null;
  }

  return (
    <div className="nc-SectionGridFeaturePlaces relative">
      {/* Header */}
      <div className="mb-8 lg:mb-12 text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#F56948]/10 to-[#F56948]/5 rounded-full border border-[#F56948]/20 mb-4">
          <Star className="w-5 h-5 text-[#F56948] fill-[#F56948]" />
          <span className="text-sm font-semibold text-[#F56948]">
            Featured Collection
          </span>
        </div>

        <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-3">
          {heading}
        </h2>

        {subHeading && (
          <p className="text-base lg:text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
            {subHeading}
          </p>
        )}
      </div>

      {/* Grid */}
      <div
        className={`grid gap-6 lg:gap-8 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 ${gridClass}`}
      >
        {properties.map((stay: StayDataType) => (
          <div
            key={stay.id}
            className="transform transition-all duration-300 hover:-translate-y-2"
          >
            <StayCard data={stay} />
          </div>
        ))}
      </div>

      {/* View All Button */}
      {properties.length >= 3 && (
        <div className="flex justify-center mt-10 lg:mt-14">
          <button
            onClick={() => (window.location.href = "/listing-stay-map")}
            className="group px-8 py-3.5 bg-white dark:bg-neutral-800 border-2 border-gray-200 dark:border-neutral-700 hover:border-[#F56948] dark:hover:border-[#F56948] rounded-full font-semibold text-gray-900 dark:text-white transition-all duration-300 shadow-sm hover:shadow-md flex items-center gap-2"
          >
            <span>Explore All Properties</span>
            <svg
              className="w-5 h-5 transform group-hover:translate-x-1 transition-transform"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 8l4 4m0 0l-4 4m4-4H3"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default SectionGridFeaturePlaces;